import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ConvertHelper } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { CacheStorageAdapter } from '@barthauer/abc/core/data';
import { ErrorHandlingService } from '@barthauer/abc/core/error-handling';
import { ResourceManagerService } from '@barthauer/abc/core/resources';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { AuthConfiguration } from '../models/auth-config.model';
import { WebApiEditorComponentInfo } from '../webapi-editor/webapi-editor-component-info';
import { WebApiInfoService } from './webapi-info.service';
import { WebApiService } from './webapi.service';

@Injectable()
export class AuthConfigService extends WebApiService {
  public static readonly configMaxLifetimeMinutes: number = 15;
  private currentAuthConfig: AuthConfiguration;
  private queryAuthConfigSubjects: Subject<AuthConfiguration>[] = [];

  constructor(
    abcConfigService: AbcConfigService,
    httpHandler: HttpBackend,
    private error: ErrorHandlingService,
    private webApiInfoService: WebApiInfoService,
    private router: Router,
  ) {
    super(new HttpClient(httpHandler), abcConfigService);
  }
  private get API_URL_GetConfiguration(): string {
    return this.abcConfigService.configuration.WebApi.ServiceURLs.Account_GetConfiguration;
  }

  public get current(): Observable<AuthConfiguration> {
    return this.getCurrent(false);
  }

  public getCurrent(useLastConfigurationIfExists: boolean): Observable<AuthConfiguration> {
    const url = this.API_URL_GetConfiguration;
    if (!this.currentAuthConfig && (CacheStorageAdapter.useOfflineMode || useLastConfigurationIfExists)) {
      this.currentAuthConfig = this.loadLastAuthConfiguration(url);
    }
    return of(this.currentAuthConfig).pipe(
      concatMap((cfg) => {
        if (
          cfg &&
          cfg.webApiUrlUsedForQuery === url &&
          (cfg.queryAgeMinutes < AuthConfigService.configMaxLifetimeMinutes ||
            CacheStorageAdapter.useOfflineMode ||
            useLastConfigurationIfExists ||
            this.queryAuthConfigSubjects.length > 0)
        ) {
          return of(cfg);
        }
        return this.queryAuthConfiguration(url).pipe(
          tap((cfg) => {
            this.currentAuthConfig = cfg;
          }),
        );
      }),
    );
  }

  private queryAuthConfiguration(url: string): Observable<AuthConfiguration> {
    const newSubject = new Subject<AuthConfiguration>();
    if (this.queryAuthConfigSubjects.length === 0) {
      this.queryAuthConfigSubjects.push(newSubject);
      this.executeWebApiPOST(url, { OutputMode: 'Json' }, true).subscribe(
        (response) => {
          let needsAuthentication: boolean;

          if (this.abcConfigService?.configuration?.WebApi?.AnonymousLogin) {
            needsAuthentication = false; // Config file value has priority only if anonymous login allowed there
          } else {
            needsAuthentication = !ConvertHelper.toBoolean(response['AllowAnonymousAccess']);
          }

          ResourceManagerService.setFixedCulture(response['FixedCulture']);

          const result = new AuthConfiguration(
            needsAuthentication,
            ConvertHelper.toBoolean(response['AllowAnyAnonymousAccess']),
            ConvertHelper.toBoolean(response['UseLocalSecurityContext']),
            ConvertHelper.toBoolean(response['UseRemoteSecurityContext']),
            ConvertHelper.toBoolean(response['AllowIntegratedSecurity']),
            <string>response['FixedCulture'],
            new Date(),
            url,
            <number>response['ClientPingIntervalMinutes'],
            <number>response['ClientPingTimeoutMilliseconds'],
            <string>response['RequestContentEncoding'],
            <string>response['Version'],
            <string>(<{}>response['SystemInfo'] ?? {})['Name'],
          );
          localStorage.setItem(url, JSON.stringify(result));
          this.notifyAuthConfiguration(result);
        },
        (err) => {
          const cfg = this.loadLastAuthConfiguration(url);
          if (err instanceof HttpErrorResponse) {
            const httpError: HttpErrorResponse = <HttpErrorResponse>err;
            if (
              httpError.message &&
              httpError.error.ExceptionMessage?.toLowerCase().startsWith('system license not available')
            ) {
              this.error.publish(err);
            } else if (
              !WebApiEditorComponentInfo.isCurrentPageWebApiEditorPage(this.router) &&
              !CacheStorageAdapter.useOfflineMode
            ) {
              this.error.publish(err);
              this.webApiInfoService.navigateToWebApiEditorPage();
            }
          }
          this.notifyAuthConfiguration(cfg);
        },
      );
    } else {
      this.queryAuthConfigSubjects.push(newSubject);
    }
    return newSubject.asObservable();
  }

  private loadLastAuthConfiguration(url: string): AuthConfiguration {
    const value = localStorage.getItem(url);
    let cfg: AuthConfiguration;
    if (value) {
      const o = JSON.parse(value);
      cfg = new AuthConfiguration(
        o.needsAuthentication,
        o.allowAnyAnonymousAccess,
        o.useLocalSecurityContext,
        o.useRemoteSecurityContext,
        o.allowIntegratedSecurity,
        o.fixedCulture,
        new Date(),
        url,
        o.clientPingIntervalMinutes,
        o.clientPingTimeoutMilliseconds,
        o.requestContentEncoding,
        o.version,
        o.systemName,
      );
    }
    return cfg;
  }

  private notifyAuthConfiguration(result: AuthConfiguration): void {
    let s: Subject<AuthConfiguration>;
    while ((s = this.queryAuthConfigSubjects.pop())) {
      s.next(result);
      s.complete();
    }
  }
}
